/**
 * Helpers for the Post Stream Review toolbar's "View changes" button, which drives
 * `editor-plugin-show-diff`'s `showDiff` with `{ originalDoc, steps }` — the
 * pre-edit doc and forward (`original → live`) steps (non-inverted contract).
 *
 * Instead of a JSON tree-diff, we reconstruct the pre-edit doc from stored data:
 * take the live doc, replace the segment range(s) with the stored
 * `originalUserContentSlice` (a throwaway `tr`) to get `originalDoc`, then derive
 * the forward steps as the INVERSE of that transform
 * (`tr.steps.map((s, i) => s.invert(tr.docs[i])).reverse()`). Applying them to
 * `originalDoc` reproduces the live doc exactly, so no bias-sensitive re-mapping
 * of the new range is needed.
 *
 * Pure: never dispatches, mutates state, or opens the modal. The caller forwards
 * the result to `showDiff(...)` with `isInverted: false`.
 */
import type { Node as PMNode } from '@atlaskit/editor-prosemirror/model';
import type { Step as ProseMirrorStep } from '@atlaskit/editor-prosemirror/transform-override';
import type { EditorView } from '@atlaskit/editor-prosemirror/view';

import type { AIContentPositionEntry } from '../../../../pm-plugins/ai-session-context/types';

import { appliedSliceOf, entryCanUndo, originalSliceOf } from './segmentSlices';

/**
 * Build the `{ originalDoc, forward steps }` pair needed by `showDiff` for a
 * single Post Stream Review segment.
 *
 * Returns `null` when the segment can't be safely diffed (no stored original
 * slice, or positions are no longer valid against the live doc, or the schema
 * rejects either of the two throwaway replaces).
 *
 * Algorithm:
 *   1. Take the live `editorView.state.doc`.
 *   2. Build throwaway transform A: replace `[segment.startPos, segment.endPos]`
 *      with `segment.originalUserContentSlice`. The result `tr.doc` is the
 *      reconstructed pre-edit doc — this is our `originalDoc`. Use mapping
 *      bias `-1` on the end position so it anchors at the end of the replaced
 *      range (matches the convention in `ai-session-context-plugin.ts`).
 *   3. The forward `original → live` steps are the INVERSE of transform A
 *      (invert each step against the doc it applied to, reversed). Applying them
 *      to `originalDoc` reproduces the live doc exactly.
 *   4. Return `{ originalDoc, steps }`. The caller passes `isInverted: false`.
 *
 * Why this is correct even after undos / collaborator edits:
 *   - The live doc is always read fresh (`state.tr` uses `state.doc`).
 *   - `segment.startPos` / `segment.endPos` are continuously remapped by the
 *     `ai-session-context` plugin's `apply()` method.
 *   - `segment.originalUserContentSlice` and `segment.newSlice` are immutable.
 *
 * The caller MUST disable the button when the segment is undone (so the live
 * doc already shows the original content) — diffing in that case would yield
 * empty steps, which is wasted work AND would render an empty diff overlay.
 */
export const buildDiffParamsForSegment = (
	editorView: EditorView,
	segment: AIContentPositionEntry,
): { originalDoc: PMNode; steps: ProseMirrorStep[] } | null => {
	const { state } = editorView;
	const { doc } = state;

	if (
		!segment?.slices ||
		segment.slices.length === 0 ||
		// `== null` (not `!`): `startPos === 0` is a valid PM document position
		// (the very start of the doc). Using `!startPos` would silently disable
		// View Changes for any segment whose content begins at position 0.
		segment.startPos == null ||
		segment.endPos == null
	) {
		return null;
	}

	// Bounds check: `tr.replaceRange` would throw on out-of-bounds positions.
	if (
		segment.startPos < 0 ||
		segment.endPos < segment.startPos ||
		segment.endPos > doc.content.size
	) {
		return null;
	}

	// Reconstruct the pre-edit doc for this segment as a *throwaway* transform —
	// we never dispatch it. `state.tr` is the canonical way to get a Transform
	// pre-configured with the right schema + doc.
	try {
		const tr = state.tr;
		tr.replaceRange(segment.startPos, segment.endPos, originalSliceOf(segment));
		const originalDoc = tr.doc;

		// The forward `original → live` steps are the INVERSE of the reconstruction:
		// invert each step against the doc it applied to and reverse the order.
		// Applying them to `originalDoc` reproduces the live doc EXACTLY
		// (ProseMirror's invert contract), so `showDiff`'s stepped-doc === live
		// check always holds — no bias-sensitive re-mapping of the new range, which
		// mis-brackets zero-width (remove) ranges and ranges adjacent to inserts.
		const steps = tr.steps.map((step, index) => step.invert(tr.docs[index])).reverse();

		return { originalDoc, steps };
	} catch {
		// Most likely cause: schema rejected the original slice because the
		// surrounding context has changed in a way that no longer accepts it.
		// We bail out — the caller will leave the button as a no-op.
		// TODO: CCI-15904 - fire a `viewChangesDiffFailed` analytics event from
		// the caller so we can monitor real-world breakage rates.
	}
	return null;
};

/**
 * Build the `{ originalDoc, forward steps }` pair needed by
 * `showDiff` for the AGGREGATE "all changes" view — i.e. every AI segment at
 * once, used when the Post Stream Review modal first opens.
 *
 * Same idea as {@link buildDiffParamsForSegment} but for N entries:
 *   1. Reconstruct the full pre-edit doc by replacing EACH entry's
 *      `[startPos, endPos]` with its `originalUserContentSlice`. We apply the
 *      entries back-to-front (descending `startPos`) in a single throwaway
 *      transform so each replace doesn't invalidate the positions of the ones
 *      not yet processed. The result is our `originalDoc`.
 *   2. Derive the forward `original → live` steps as the INVERSE of pass 1
 *      (invert each step against the doc it applied to, reversed). Applying them
 *      to `originalDoc` reproduces the live doc exactly.
 *
 * Entries currently at their original slice (`appliedIndex === 0`) are skipped for
 * BOTH passes: their live range already shows the original content, so they
 * contribute no diff.
 *
 * Returns `null` when there is nothing to diff (no applicable entries) or when a
 * replace throws (schema rejection / stale positions) — the caller then leaves
 * "View changes" as a no-op.
 *
 * The helper is pure: it never dispatches, mutates state, or opens the modal.
 */
/**
 * Collapse coarse entries whose live ranges OVERLAP into a single effective
 * entry. This is the normaliser the Post Stream Review screen runs ONCE when it
 * derives `coarseSegments` from `aiContentPositions`, so every downstream
 * consumer (segmenting, aggregate "View changes", aggregate undo/redo) sees
 * disjoint segments and never has to handle overlap itself.
 *
 * Overlap only happens when the AI edits the SAME node across multiple tool
 * calls within one session (e.g. a table refined three times, or a panel updated
 * then replaced): each commit records an entry over the (remapped) same range.
 * Consumers that reconstruct the pre-edit doc by replaying every entry's original
 * slice back-to-front then break — the overlapping ranges shift each other's
 * positions, corrupting the doc and producing out-of-range steps.
 *
 * The net change for such a region is `earliest original → latest new`, so each
 * overlapping group collapses to a single entry: the LATEST entry's range + applied
 * slice (which reflects the current content of the region) and the EARLIEST entry's
 * original slice (the pre-session content), emitted as a fresh two-slice history at
 * `appliedIndex` 1 (overlap merging runs at open, before any undo). Disjoint entries
 * (distinct nodes) each form their own group and are returned unchanged, so the
 * common case is unaffected.
 *
 * "earliest" / "latest" are by original array (tool-call commit) order, which is
 * why the index is threaded through the sort.
 */
export const mergeOverlappingSegments = (
	segments: AIContentPositionEntry[],
): AIContentPositionEntry[] => {
	const indexed = segments.map((seg, index) => ({ seg, index }));
	// Sort by start (then commit order) so overlapping entries sit adjacent.
	indexed.sort((a, b) => a.seg.startPos - b.seg.startPos || a.index - b.index);

	const merged: AIContentPositionEntry[] = [];
	let group: Array<{ index: number; seg: AIContentPositionEntry }> = [];
	let groupEnd = -1;

	const flush = () => {
		if (group.length === 0) {
			return;
		}
		if (group.length === 1) {
			merged.push(group[0].seg);
		} else {
			const earliest = group.reduce((a, b) => (a.index <= b.index ? a : b));
			const latest = group.reduce((a, b) => (a.index >= b.index ? a : b));
			// The net change for the overlap group is earliest-original → latest-applied.
			// Emit a fresh two-slice history with the applied (new) slice selected.
			const originalSlice = originalSliceOf(earliest.seg);
			const appliedSlice = appliedSliceOf(latest.seg);
			merged.push({
				startPos: latest.seg.startPos,
				endPos: latest.seg.endPos,
				slices: [originalSlice, appliedSlice],
				appliedIndex: 1,
				kind:
					originalSlice.content.size === 0
						? 'add'
						: appliedSlice.content.size === 0
							? 'remove'
							: 'update',
			});
		}
		group = [];
	};

	for (const item of indexed) {
		// A strictly-earlier start that is still before the running group end means
		// the ranges overlap (touching at a boundary — `startPos === groupEnd` — is
		// NOT an overlap; adjacent distinct nodes must stay separate).
		if (group.length > 0 && item.seg.startPos >= groupEnd) {
			flush();
		}
		group.push(item);
		groupEnd = group.length === 1 ? item.seg.endPos : Math.max(groupEnd, item.seg.endPos);
	}
	flush();

	return merged;
};

export const buildDiffParamsForAllSegments = (
	editorView: EditorView,
	segments: AIContentPositionEntry[],
): { originalDoc: PMNode; steps: ProseMirrorStep[] } | null => {
	const { state } = editorView;
	const { doc } = state;

	const applicable = (segments ?? []).filter(
		(s) =>
			s &&
			// Only entries currently showing a change (not at their original slice)
			// contribute to the diff; `appliedIndex === 0` already shows the original.
			entryCanUndo(s) &&
			s.slices &&
			s.startPos != null &&
			s.endPos != null &&
			s.startPos >= 0 &&
			s.endPos >= s.startPos &&
			s.endPos <= doc.content.size,
	);

	if (applicable.length === 0) {
		return null;
	}

	try {
		// Pass 1 — reconstruct the pre-edit doc. Apply originals back-to-front so
		// earlier replaces don't shift the positions of later (earlier-in-doc) ones.
		//
		// This relies on `segments` being DISJOINT — overlapping entries (the same
		// node edited by multiple tool calls) must be collapsed by
		// `mergeOverlappingSegments` before they reach here, otherwise the
		// back-to-front replaces corrupt each other's positions. The Post Stream
		// Review screen does this once when deriving `coarseSegments`.
		const descending = [...applicable].sort((a, b) => b.startPos - a.startPos);
		const tr = state.tr;
		for (const seg of descending) {
			tr.replaceRange(seg.startPos, seg.endPos, originalSliceOf(seg));
		}
		const originalDoc = tr.doc;

		// Pass 2 — forward (original → live) steps are the INVERSE of pass 1: invert
		// each step against the doc it applied to, reversed. Applying them to
		// `originalDoc` reproduces the live doc EXACTLY (PM invert contract).
		//
		// Re-applying each `newSlice` at a re-mapped range (the previous approach)
		// drifts when an inserted segment and an adjacent removal map to coincident
		// positions — the removal's range engulfs the just-inserted content, so the
		// stepped doc ≠ live and `showDiff` renders nothing. The inverse can't drift.
		const steps = tr.steps.map((step, index) => step.invert(tr.docs[index])).reverse();

		return { originalDoc, steps };
	} catch {
		// Schema rejection or stale positions — leave View changes as a no-op.
	}
	return null;
};
